const BASE_URL = '/api/v1';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `Request failed: ${res.status}`);
  }
  return res.json();
}

// Auth
export const login = (phoneNumber: string) =>
  request<{ user: import('../types').User; token: string }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ phoneNumber }),
  });

export const logout = () =>
  request<{ success: boolean }>('/auth/logout', { method: 'POST' });

// Events
export const getEvents = (from: string, to: string) =>
  request<{ events: import('../types').CalendarEvent[] }>(`/events?from=${from}&to=${to}`);

export const getToday = () =>
  request<{
    event: import('../types').CalendarEvent | null;
    nextEvent?: import('../types').CalendarEvent | null;
    attendance?: import('../types').AttendanceRecord[];
    driving?: import('../types').DrivingAssignment | null;
  }>('/events/today');

export const getEvent = (id: string) =>
  request<{
    event: import('../types').CalendarEvent;
    attendance: import('../types').AttendanceRecord[];
    driving: import('../types').DrivingAssignment | null;
  }>(`/events/${id}`);

export const createEvent = (data: Partial<import('../types').CalendarEvent>) =>
  request<{ event: import('../types').CalendarEvent }>('/events', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateEvent = (id: string, data: Partial<import('../types').CalendarEvent>) =>
  request<{ event: import('../types').CalendarEvent }>(`/events/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const deleteEvent = (id: string) =>
  request<{ success: boolean }>(`/events/${id}`, { method: 'DELETE' });

export const createBulkEvents = (events: Partial<import('../types').CalendarEvent>[]) =>
  request<{ events: import('../types').CalendarEvent[] }>('/events/bulk', {
    method: 'POST',
    body: JSON.stringify({ events }),
  });

// Attendance
export const getAttendance = (eventId: string) =>
  request<{ attendance: import('../types').AttendanceRecord[] }>(`/events/${eventId}/attendance`);

export const updateAttendance = (eventId: string, kidId: string, status: import('../types').AttendanceStatus) =>
  request<{ attendance: import('../types').AttendanceRecord }>(`/events/${eventId}/attendance`, {
    method: 'PUT',
    body: JSON.stringify({ kidId, status }),
  });

// Driving
export const claimDriving = (eventId: string, type?: 'pickup' | 'return', pickupSpot?: string) =>
  request<{ driving: import('../types').DrivingAssignment }>(`/events/${eventId}/driver`, {
    method: 'POST',
    body: JSON.stringify({ type, pickupSpot }),
  });

export const releaseDriving = (eventId: string, type?: 'pickup' | 'return') =>
  request<{ driving: import('../types').DrivingAssignment }>(`/events/${eventId}/driver?type=${type || 'pickup'}`, {
    method: 'DELETE',
  });

export const getDrivingStats = () =>
  request<{ stats: import('../types').DrivingStat[] }>('/driving/stats');

// Group
export const getGroup = () =>
  request<{
    group: import('../types').Group;
    members: import('../types').User[];
    kids: import('../types').Kid[];
  }>('/group');

export const addMember = (name: string, phoneNumber: string) =>
  request<{ member: import('../types').User }>('/group/members', {
    method: 'POST',
    body: JSON.stringify({ name, phoneNumber }),
  });

export const updateMember = (id: string, data: { name?: string }) =>
  request<{ member: import('../types').User }>(`/group/members/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const deleteMember = (id: string) =>
  request<{ success: boolean }>(`/group/members/${id}`, { method: 'DELETE' });

export const addKid = (name: string, parentId: string) =>
  request<{ kid: import('../types').Kid }>('/group/kids', {
    method: 'POST',
    body: JSON.stringify({ name, parentId }),
  });

export const updateKid = (id: string, data: { name?: string }) =>
  request<{ kid: import('../types').Kid }>(`/group/kids/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const deleteKid = (id: string) =>
  request<{ success: boolean }>(`/group/kids/${id}`, { method: 'DELETE' });

// Locations
export const getLocations = () =>
  request<{ locations: import('../types').Location[] }>('/locations');

export const createLocation = (name: string, address: string) =>
  request<{ location: import('../types').Location }>('/locations', {
    method: 'POST',
    body: JSON.stringify({ name, address }),
  });

export const updateLocation = (id: string, data: { name?: string; address?: string }) =>
  request<{ location: import('../types').Location }>(`/locations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const deleteLocation = (id: string) =>
  request<{ success: boolean }>(`/locations/${id}`, { method: 'DELETE' });

// Availability
export const setAvailability = (eventId: string, status: import('../types').AvailabilityStatus) =>
  request<{ availability: import('../types').AvailabilityRecord }>(`/events/${eventId}/availability`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });

export const getAvailability = (eventId: string) =>
  request<{ availability: import('../types').AvailabilityRecord[] }>(`/events/${eventId}/availability`);

// Weekly Plan
export const generateWeeklyPlan = (weekStart: string, weekEnd: string) =>
  request<{ plan: import('../types').WeeklyPlan }>('/weekly-plan/generate', {
    method: 'POST',
    body: JSON.stringify({ weekStart, weekEnd }),
  });

export const getWeeklyPlan = (week: string) =>
  request<{ plan: import('../types').WeeklyPlan | null }>(`/weekly-plan?week=${week}`);

export const publishWeeklyPlan = (planId: string) =>
  request<{ plan: import('../types').WeeklyPlan }>(`/weekly-plan/${planId}/publish`, {
    method: 'PUT',
  });

export const updatePlanAssignment = (planId: string, eventId: string, data: { pickupDriverId?: string | null; returnDriverId?: string | null }) =>
  request<{ plan: import('../types').WeeklyPlan }>(`/weekly-plan/${planId}/assignment/${eventId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const shareWeeklyPlan = (planId: string) =>
  request<{ plan: import('../types').WeeklyPlan }>(`/weekly-plan/${planId}/share`, {
    method: 'PUT',
  });

export const submitPlanFeedback = (planId: string, status: 'approved' | 'concerns', comment?: string) =>
  request<{ feedback: import('../types').PlanFeedback }>(`/weekly-plan/${planId}/feedback`, {
    method: 'POST',
    body: JSON.stringify({ status, comment }),
  });

export const getPlanFeedback = (planId: string) =>
  request<{
    feedback: import('../types').PlanFeedback[];
    summary: import('../types').PlanFeedbackSummary | null;
  }>(`/weekly-plan/${planId}/feedback`);

// Profile
export const getProfile = () =>
  request<{
    user: import('../types').User;
    kids: import('../types').Kid[];
    drivingStats: { pickupDrives: number; returnDrives: number; totalDrives: number; lastDrive: string | null };
  }>('/profile');

export const updateProfile = (name: string) =>
  request<{ user: import('../types').User }>('/profile', {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
